import React, { useEffect, useState } from 'react';
import { ResponsiveCalendar } from '@nivo/calendar';
import { useTheme } from '../ThemeContext';
import styles from './Dashboard.module.css';
import BarGraph from '../graphs/BarGraph';
import CardContent from '@mui/material/CardContent';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import { DataGrid } from '@mui/x-data-grid';
import Cal from '../graphs/Cal';
import Summary from '../graphs/Summary';

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Dashboard = () => {
  const { theme } = useTheme();
  const [locationData, setLocationData] = useState([]);
  const [supportHistoryData, setSupportHistoryData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dashboardResponse = await fetch(`/api/`);
        if (!dashboardResponse.ok) {
          throw new Error(`Dashboard request failed with ${dashboardResponse.status}`);
        }

        const dashboardResult = await dashboardResponse.json();
        if (Array.isArray(dashboardResult?.locationTable)) {
          setLocationData(dashboardResult.locationTable);
          setSupportHistoryData(Array.isArray(dashboardResult?.supportHistoryTable) ? dashboardResult.supportHistoryTable : []);
        } else {
          console.error('Invalid or no location data received:', dashboardResult);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchData();
  }, []);

  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1, 12, 0, 0, 0);
  const endOfYear = new Date(today.getFullYear(), 11, 31, 12, 0, 0, 0);

  const dateMap = new Map();
  locationData
    .filter((item) => item.SupportEndDate && [2, 3].includes(Number(item.SupportStatus)))
    .forEach((item) => {
      const [year, month, day] = item.SupportEndDate.split("T")[0].split("-");
      const formattedDate = new Date(Number(year), Number(month) - 1, Number(day));

      if (!Number.isNaN(formattedDate.getTime())) {
        const dateKey = formattedDate.getTime();
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, {
            expiringCompanies: [],
            addedCompanies: [],
          });
        }
        dateMap.get(dateKey).expiringCompanies.push(item.CompanyAndDivision);
      }
    });

  locationData
    .filter((item) => item.SupportStartDate && [2, 3].includes(Number(item.SupportStatus)))
    .forEach((item) => {
      const [year, month, day] = item.SupportStartDate.split("T")[0].split("-");
      const formattedDate = new Date(Number(year), Number(month) - 1, Number(day));

      if (!Number.isNaN(formattedDate.getTime())) {
        const dateKey = formattedDate.getTime();
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, {
            expiringCompanies: [],
            addedCompanies: [],
          });
        }
        dateMap.get(dateKey).addedCompanies.push(item.CompanyAndDivision);
      }
    });

  const calendarData = Array.from(dateMap.entries())
    .map(([timestamp, details]) => {
      const date = new Date(Number(timestamp));
      if (Number.isNaN(date.getTime()) || date < startOfYear || date > endOfYear) {
        return null;
      }

      const expiringCount = details.expiringCompanies.length;
      const addedCount = details.addedCompanies.length;
      const value = expiringCount > 0 && addedCount > 0
        ? 0
        : expiringCount > 0
          ? expiringCount
          : -addedCount;

      return {
        day: formatDateKey(date),
        value,
        expiringCount,
        addedCount,
        expiringCompanies: details.expiringCompanies,
        addedCompanies: details.addedCompanies,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.day.localeCompare(b.day));

  const cardBackground = theme === 'dark' ? '#0e1317' : '#fff';
  const textColor = theme === 'dark' ? '#fff' : '#000';
  const calendarTheme = theme === 'dark'
    ? {
        text: { fill: '#ffffff', fontSize: 11, outlineWidth: 0, outlineColor: 'transparent' },
        labels: { text: { fill: '#ffffff' } },
        tooltip: { container: { background: '#101924', color: '#f8fbff', fontSize: 12 } },
      }
    : {
        text: { fill: '#1f2937', fontSize: 11, outlineWidth: 0, outlineColor: 'transparent' },
        labels: { text: { fill: '#1f2937' } },
        tooltip: { container: { background: '#ffffff', color: '#1f2937', fontSize: 12 } },
      };
  const emptyColor = theme === 'dark' ? '#253140' : '#edf2f7';
  const borderColor = theme === 'dark' ? '#415266' : '#d1d9e6';
  const calendarColorScale = (value) => {
    if (value === 0) {
      return theme === 'dark' ? '#f59e0b' : '#d97706';
    }

    if (value > 0) {
      return theme === 'dark' ? '#ef4444' : '#dc2626';
    }

    return theme === 'dark' ? '#22c55e' : '#16a34a';
  };
  const historyCardBackground = theme === 'dark' ? '#0e1317' : '#ffffff';
  const historyMutedText = theme === 'dark' ? '#b8c3cf' : '#5b6472';
  const getSupportStatusLabel = (status) => {
    switch (Number(status)) {
      case 2:
        return 'Standard';
      case 3:
        return 'Premium';
      default:
        return 'Basic';
    }
  };

  const formatDate = (value) => {
    if (!value) {
      return 'N/A';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 'N/A';
    }

    return parsed.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const historyRows = supportHistoryData.map((entry) => ({
    id: entry.LocationSupportHistoryID,
    companyAndDivision: entry.CompanyAndDivision,
    isExpired: entry.ChangeReason === 'ExpiredAutoRevert',
    previousSupportStatus: getSupportStatusLabel(entry.PreviousSupportStatus),
    newSupportStatus: getSupportStatusLabel(entry.NewSupportStatus),
    previousSupportStartDate: formatDate(entry.PreviousSupportStartDate),
    previousSupportEndDate: formatDate(entry.PreviousSupportEndDate),
    changeType: entry.ChangeReason === 'ExpiredAutoRevert' ? 'Auto Expire' : 'Manual Change',
    changedByEmployeeName: entry.ChangedByEmployeeName || 'System',
  }));

  const historyColumns = [
    {
      field: 'companyAndDivision',
      headerName: 'Location',
      flex: 1.5,
      minWidth: 260,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>{params.value}</span>
          {params.row.isExpired ? (
            <span style={{ color: '#dc2626' }}>(Expired)</span>
          ) : null}
        </Box>
      ),
    },
    { field: 'previousSupportStatus', headerName: 'Previous Plan', flex: 1, minWidth: 150 },
    { field: 'newSupportStatus', headerName: 'New Plan', flex: 1, minWidth: 140 },
    { field: 'previousSupportStartDate', headerName: 'Previous Start', flex: 1, minWidth: 140 },
    { field: 'previousSupportEndDate', headerName: 'Previous End', flex: 1, minWidth: 140 },
    { field: 'changeType', headerName: 'Change Type', flex: 1, minWidth: 150 },
    { field: 'changedByEmployeeName', headerName: 'Changed By', flex: 1, minWidth: 160 },
  ];

  return (
    <div className={`${styles.all} ${styles[theme]}`}>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div className={`${styles.cal} ${styles[theme]}`}><Cal theme={theme} /></div>
        </div>
        <div className={`${styles.barGraph} ${styles[theme]}`}><BarGraph theme={theme} /></div>
        <div className={styles[theme]} style={{ marginLeft: '0px', marginTop: '10px' }}>
          <Card sx={{ height: '258px', backgroundColor: cardBackground }}>
            <CardContent>
              <Typography variant="h7" sx={{ fontSize: 17, color: textColor }}>
                Calendar (Plans That Started and Expire This Year)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1, color: textColor, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: theme === 'dark' ? '#22c55e' : '#16a34a' }} />
                  <Typography variant="caption" sx={{ color: textColor }}>
                    Green = plans started
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: theme === 'dark' ? '#ef4444' : '#dc2626' }} />
                  <Typography variant="caption" sx={{ color: textColor }}>
                    Red = plans expiring
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: theme === 'dark' ? '#f59e0b' : '#d97706' }} />
                  <Typography variant="caption" sx={{ color: textColor }}>
                    Orange = both on the same day
                  </Typography>
                </Box>
              </Box>
              <div style={{ width: '880px', height: '190px', marginTop: '8px' }}>
                <ResponsiveCalendar
                  data={calendarData}
                  from={startOfYear}
                  to={endOfYear}
                  theme={calendarTheme}
                  emptyColor={emptyColor}
                  colorScale={calendarColorScale}
                  margin={{ top: 16, right: 12, bottom: 12, left: 36 }}
                  yearSpacing={0}
                  monthSpacing={6}
                  monthBorderWidth={0}
                  monthLegendOffset={8}
                  dayBorderWidth={1}
                  dayBorderColor={borderColor}
                  dayRadius={2}
                  direction="horizontal"
                  align="center"
                  tooltip={({ day, value, data }) => {
                    const item = data;
                    const label = new Date(day).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    });

                    return (
                      <div style={{ padding: '10px 12px', maxWidth: '320px' }}>
                        <div style={{ fontWeight: 700 }}>{label}</div>
                        {item?.expiringCount ? (
                          <div>{item.expiringCount} expiring plan{item.expiringCount === 1 ? '' : 's'}</div>
                        ) : null}
                        {item?.addedCount ? (
                          <div>{item.addedCount} added plan{item.addedCount === 1 ? '' : 's'}</div>
                        ) : null}
                        {item?.expiringCompanies?.length ? (
                          <div style={{ marginTop: '6px' }}>
                            <strong>Expiring:</strong> {item.expiringCompanies.join(', ')}
                          </div>
                        ) : null}
                        {item?.addedCompanies?.length ? (
                          <div style={{ marginTop: '6px' }}>
                            <strong>Added:</strong> {item.addedCompanies.join(', ')}
                          </div>
                        ) : null}
                      </div>
                    );
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className={`${styles.summary} ${styles[theme]}`}><Summary theme={theme} /></div>

      <aside className={styles.dashboardHistorySection}>
        <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
          <Box sx={{ m: 1, width: '100%' }}>
            <Paper elevation={1}>
              <Box
                className={styles[theme]}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '8px',
                  paddingBottom: '0px',
                  backgroundColor: theme === 'dark' ? '#000' : '#fff',
                  color: theme === 'dark' ? '#D8D8D8' : '#000',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'DM Serif Display, serif',
                      fontWeight: '400',
                      fontStyle: 'normal',
                      textAlign: 'left',
                      paddingLeft: '10px'
                    }}
                    variant="h6"
                    gutterBottom
                  >
                    SUPPORT HISTORY
                  </Typography>
                  <Typography
                    sx={{
                      paddingLeft: '10px',
                      paddingBottom: '8px',
                      textAlign: 'left',
                      color: historyMutedText,
                    }}
                    variant="overline"
                  >
                    Manual and automatic support plan changes by location
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <TableContainer className={theme === 'light' ? 'light-theme' : 'dark-theme'} style={{ height: '420px', overflowY: 'auto' }}>
              <DataGrid
                className="my-datagrid"
                rows={historyRows}
                columns={historyColumns}
                initialState={{ pagination: { paginationModel: { page: 0, pageSize: 8 } } }}
                pageSizeOptions={[8, 12]}
                rowHeight={36}
                headerHeight={38}
                disableRowSelectionOnClick
                sx={{
                  border: 0,
                  backgroundColor: theme === 'dark' ? '#000' : '#F8FAFC',
                  color: theme === 'dark' ? '#D8D8D8' : '#000',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: theme === 'dark' ? '#0B192C' : '#F8FAFC',
                    color: theme === 'dark' ? '#D8D8D8' : '#000',
                    fontWeight: 700,
                  },
                  '& .MuiDataGrid-cell': {
                    backgroundColor: theme === 'dark' ? '#000' : '#F8FAFC',
                    color: theme === 'dark' ? '#D8D8D8' : '#000',
                    borderColor: theme === 'dark' ? '#2c2c2b' : '#e5e7eb',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: theme === 'dark' ? '#D8D8D8' : '#000',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    backgroundColor: theme === 'dark' ? '#000' : '#F8FAFC',
                    borderColor: theme === 'dark' ? '#2c2c2b' : '#e5e7eb',
                  },
                  '& .MuiDataGrid-row': {
                    backgroundColor: theme === 'dark' ? '#000' : '#F8FAFC',
                  },
                  '& .MuiTablePagination-root, & .MuiSvgIcon-root': {
                    color: theme === 'dark' ? '#D8D8D8' : '#000',
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Box sx={{ p: 2, color: theme === 'dark' ? '#D8D8D8' : historyMutedText, backgroundColor: theme === 'dark' ? '#000' : '#F8FAFC' }}>
                      No support history recorded yet.
                    </Box>
                  ),
                }}
              />
            </TableContainer>
          </Box>
        </Box>
      </aside>
    </div>
  );
};

export default Dashboard;
